import React, { useState, useRef, useEffect } from 'react';
import './CustomDatePicker.css';

interface CustomDatePickerProps {
  value?: string;
  onChange: (date: string) => void;
  placeholder?: string;
  className?: string;
  id?: string;
}

export const CustomDatePicker: React.FC<CustomDatePickerProps> = ({
  value = '',
  onChange,
  placeholder = 'Select date',
  className = '',
  id
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentDate, setCurrentDate] = useState(() => {
    if (value) {
      try {
        const date = new Date(value);
        return !isNaN(date.getTime()) ? new Date(date.getFullYear(), date.getMonth(), 1) : new Date();
      } catch {
        return new Date();
      }
    }
    // Default to a more reasonable birth year (30 years ago) instead of current year
    const reasonableBirthYear = new Date().getFullYear() - 30;
    return new Date(reasonableBirthYear, 0, 1); // January 1st of 30 years ago
  });
  const [selectedDate, setSelectedDate] = useState<Date | null>(
    value ? new Date(value) : null
  );
  const [showMonthSelector, setShowMonthSelector] = useState(false);
  const [showYearSelector, setShowYearSelector] = useState(false);
  const [inputValue, setInputValue] = useState(() => {
    if (value) {
      try {
        const date = new Date(value);
        return !isNaN(date.getTime()) ? date.toLocaleDateString('en-GB') : '';
      } catch {
        return '';
      }
    }
    return '';
  });
  const containerRef = useRef<HTMLDivElement>(null);

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // Sync external value prop with internal state
  useEffect(() => {
    if (value && value !== formatDate(selectedDate)) {
      try {
        const date = new Date(value);
        if (!isNaN(date.getTime())) {
          setSelectedDate(date);
          setInputValue(date.toLocaleDateString('en-GB')); // DD/MM/YYYY format
          setCurrentDate(new Date(date.getFullYear(), date.getMonth(), 1));
        }
      } catch (error) {
        // Invalid date, reset
        setSelectedDate(null);
        setInputValue('');
      }
    } else if (!value && (selectedDate || inputValue)) {
      setSelectedDate(null);
      setInputValue('');
    }
  }, [value, selectedDate, inputValue]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const formatDate = (date: Date | null): string => {
    if (!date) return '';
    return date.toISOString().split('T')[0];
  };

  const getDaysInMonth = (date: Date): number => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date: Date): number => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const handleDateSelect = (day: number) => {
    const newDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
    setSelectedDate(newDate);
    const formattedDate = formatDate(newDate);
    const displayValue = newDate.toLocaleDateString('en-GB'); // DD/MM/YYYY format
    setInputValue(displayValue);
    onChange(formattedDate);
    setIsOpen(false);
  };

  const formatInputValue = (value: string) => {
    // Remove all non-digits
    const digits = value.replace(/\D/g, '');
    
    if (digits.length === 0) return '';
    if (digits.length <= 2) return digits;
    if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
    return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4, 8)}`;
  };

  // Simplified: removed overlay and display text functions

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;
    const formatted = formatInputValue(rawValue);
    setInputValue(formatted);
    
    // Check if we have a complete date
    if (formatted.length === 10) {
      const parts = formatted.split('/');
      if (parts.length === 3) {
        const day = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1;
        const year = parseInt(parts[2], 10);
        const date = new Date(year, month, day);
        
        if (!isNaN(date.getTime()) && date.getDate() === day && date.getMonth() === month && date.getFullYear() === year) {
          setSelectedDate(date);
          onChange(formatDate(date));
        }
      }
    } else {
      setSelectedDate(null);
      onChange('');
    }
  };

  const handlePrevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const handleMonthSelect = (monthIndex: number) => {
    setCurrentDate(new Date(currentDate.getFullYear(), monthIndex, 1));
    setShowMonthSelector(false);
  };

  const handleYearSelect = (year: number) => {
    setCurrentDate(new Date(year, currentDate.getMonth(), 1));
    setShowYearSelector(false);
  };

  const generateYears = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    // For date of birth, start from a more reasonable range (current year - 18 to 1930)
    // This covers adults from 18 to ~95 years old
    const startYear = currentYear - 18; // Start from 18 years ago (legal adults)
    const endYear = 1930; // Go back to 1930 (covers very elderly patients)
    
    for (let i = startYear; i >= endYear; i--) {
      years.push(i);
    }
    return years;
  };

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(currentDate);
    const firstDay = getFirstDayOfMonth(currentDate);
    const days = [];

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(
        <div key={`empty-${i}`} className="date-picker-day other-month">
          {new Date(currentDate.getFullYear(), currentDate.getMonth(), -firstDay + i + 1).getDate()}
        </div>
      );
    }

    // Add days of the current month
    for (let day = 1; day <= daysInMonth; day++) {
      const isSelected = selectedDate && 
        selectedDate.getDate() === day && 
        selectedDate.getMonth() === currentDate.getMonth() && 
        selectedDate.getFullYear() === currentDate.getFullYear();
      
      const isToday = new Date().toDateString() === new Date(currentDate.getFullYear(), currentDate.getMonth(), day).toDateString();

      days.push(
        <div
          key={day}
          className={`date-picker-day ${isSelected ? 'selected' : ''} ${isToday ? 'today' : ''}`}
          onClick={() => handleDateSelect(day)}
        >
          {day}
        </div>
      );
    }

    return days;
  };

  return (
    <div ref={containerRef} className={`date-picker-container ${className}`}>
      {/* Mobile backdrop */}
      {isOpen && (
        <div 
          className="date-picker-backdrop d-md-none"
          onClick={() => setIsOpen(false)}
        />
      )}
      <div className="date-picker-input-wrapper">
        <input
          id={id}
          type="text"
          className="form-control date-picker-input"
          value={inputValue || ''}
          onChange={handleInputChange}
          placeholder="DD/MM/YYYY"
        />
        {/* Add a dedicated calendar button for better mobile experience */}
        <button
          type="button"
          className="date-picker-toggle-btn"
          onClick={(e) => {
            e.preventDefault();
            setIsOpen(!isOpen);
          }}
          aria-label="Open calendar"
        >
          📅
        </button>
      </div>
      
      {isOpen && (
        <div className="date-picker-popup show">
          <div className="date-picker-header">
            <button
              type="button"
              className="date-picker-nav"
              onClick={handlePrevMonth}
            >
              ‹
            </button>
            <div style={{ position: 'relative' }}>
              <div 
                className="date-picker-month-year"
                onClick={() => {
                  setShowMonthSelector(!showMonthSelector);
                  setShowYearSelector(false);
                }}
              >
                {monthNames[currentDate.getMonth()]}
              </div>
              {showMonthSelector && (
                <div className="date-picker-month-selector">
                  {monthNames.map((month, index) => (
                    <div
                      key={month}
                      className={`date-picker-month-option ${index === currentDate.getMonth() ? 'selected' : ''}`}
                      onClick={() => handleMonthSelect(index)}
                    >
                      {month}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div style={{ position: 'relative' }}>
              <div 
                className="date-picker-month-year"
                onClick={() => {
                  setShowYearSelector(!showYearSelector);
                  setShowMonthSelector(false);
                }}
              >
                {currentDate.getFullYear()}
              </div>
              {showYearSelector && (
                <div className="date-picker-year-selector">
                  {generateYears().map(year => (
                    <div
                      key={year}
                      className={`date-picker-year-option ${year === currentDate.getFullYear() ? 'selected' : ''}`}
                      onClick={() => handleYearSelect(year)}
                    >
                      {year}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <button
              type="button"
              className="date-picker-nav"
              onClick={handleNextMonth}
            >
              ›
            </button>
          </div>
          
          <div className="date-picker-grid">
            {dayNames.map(day => (
              <div key={day} className="date-picker-day-header">
                {day}
              </div>
            ))}
            {renderCalendar()}
          </div>
        </div>
      )}
    </div>
  );
};